/**
 * Shared resilient API client for GeoVera WebGIS.
 * - Auth + CSRF headers
 * - Timeout via AbortSignal
 * - Retries on transient failures (idempotent / safe by default for GET)
 * - Auto CSRF ensure + one retry on invalid CSRF for mutating methods
 */

import { API_ORIGIN, DEFAULT_API_TIMEOUT_MS } from '../config/api';
import { withRetry } from './retryService';

export class ApiClientError extends Error {
  status?: number;
  body?: unknown;
  network?: boolean;

  constructor(message: string, opts?: { status?: number; body?: unknown; network?: boolean }) {
    super(message);
    this.name = 'ApiClientError';
    this.status = opts?.status;
    this.body = opts?.body;
    this.network = opts?.network;
  }
}

function readCsrfFromDocument(): string | null {
  if (typeof document === 'undefined') return null;
  // Prefer last match if multiple cookies somehow exist
  const re = /(?:^|;\s*)_csrf_token=([^;]*)/g;
  let match: RegExpExecArray | null;
  let last: string | null = null;
  while ((match = re.exec(document.cookie)) !== null) {
    last = match[1];
  }
  if (!last) return null;
  try {
    return decodeURIComponent(last.trim());
  } catch {
    return last.trim();
  }
}

/** Write-through cache so header always matches the token we just obtained */
let cachedCsrfToken: string | null = null;

function getCsrfToken(): string | null {
  if (cachedCsrfToken) return cachedCsrfToken;
  const fromDoc = readCsrfFromDocument();
  if (fromDoc) {
    cachedCsrfToken = fromDoc;
    return fromDoc;
  }
  return null;
}

/**
 * Ensure browser has a CSRF cookie and return the token for X-CSRF-Token.
 * @param forceRefresh - always hit /api/csrf-token (e.g. after Invalid CSRF)
 */
export async function ensureCsrfToken(forceRefresh = false): Promise<string | null> {
  if (!forceRefresh) {
    const existing = getCsrfToken();
    if (existing) return existing;
  } else {
    cachedCsrfToken = null;
  }

  try {
    const res = await fetch(resolveUrl('/api/csrf-token'), {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json', 'X-GeoVera-Client': 'webgis' },
      cache: 'no-store',
    });
    if (res.ok) {
      const data = (await res.json().catch(() => ({}))) as { csrfToken?: string };
      if (data.csrfToken) {
        cachedCsrfToken = data.csrfToken;
        return data.csrfToken;
      }
    }
  } catch {
    /* ignore */
  }

  // Cookie may have been set even if body parse failed
  cachedCsrfToken = null;
  const again = readCsrfFromDocument();
  cachedCsrfToken = again;
  return again;
}

export function buildAuthHeaders(extra?: HeadersInit): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-GeoVera-Client': 'webgis',
  };

  const csrf = getCsrfToken();
  if (csrf) headers['X-CSRF-Token'] = csrf;

  if (extra) {
    const e = new Headers(extra);
    e.forEach((v, k) => {
      headers[k] = v;
    });
  }
  return headers;
}

function resolveUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/api')) return `${API_ORIGIN}${path}`;
  if (path.startsWith('/')) return `${API_ORIGIN}${path}`;
  return `${API_ORIGIN}/api/${path}`;
}

export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Timeout ms (default 15s) */
  timeoutMs?: number;
  /** Retry transient failures (default: true for GET/HEAD) */
  retry?: boolean;
  /** Skip JSON content-type / stringify */
  rawBody?: BodyInit | null;
  /** Skip automatic CSRF ensure (default false for mutating methods) */
  skipCsrf?: boolean;
}

function isMutatingMethod(method: string): boolean {
  return !['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase());
}

function isCsrfFailure(status: number, bodyText: string): boolean {
  if (status !== 403) return false;
  return /csrf/i.test(bodyText);
}

/**
 * Fetch GeoVera backend with auth, timeout, optional retry, and CSRF handling.
 */
export async function apiFetch(path: string, options: ApiFetchOptions = {}): Promise<Response> {
  const {
    body,
    timeoutMs = DEFAULT_API_TIMEOUT_MS,
    retry,
    rawBody,
    headers: extraHeaders,
    skipCsrf,
    ...rest
  } = options;

  const method = (rest.method || 'GET').toUpperCase();
  const shouldRetry = retry ?? (method === 'GET' || method === 'HEAD');
  const needsCsrf = isMutatingMethod(method) && !skipCsrf;

  const doFetch = async (csrfOverride?: string | null): Promise<Response> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const headers = buildAuthHeaders(extraHeaders);
      if (csrfOverride) {
        headers['X-CSRF-Token'] = csrfOverride;
        cachedCsrfToken = csrfOverride;
      }

      const init: RequestInit = {
        ...rest,
        method,
        credentials: rest.credentials ?? 'include',
        headers,
        signal: rest.signal ?? controller.signal,
      };

      if (rawBody !== undefined) {
        init.body = rawBody;
      } else if (body !== undefined && method !== 'GET' && method !== 'HEAD') {
        init.body = JSON.stringify(body);
      }

      const response = await fetch(resolveUrl(path), init);

      // Capture refreshed CSRF from error JSON body if present
      if (response.status === 403) {
        try {
          const clone = response.clone();
          const data = (await clone.json()) as { csrfToken?: string; message?: string };
          if (data.csrfToken) {
            cachedCsrfToken = data.csrfToken;
          }
        } catch {
          /* not json */
        }
      }

      if (shouldRetry && [408, 429, 500, 502, 503, 504].includes(response.status)) {
        throw new ApiClientError(`HTTP ${response.status}`, { status: response.status });
      }

      return response;
    } catch (error) {
      if (error instanceof ApiClientError) throw error;
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ApiClientError('Request timed out', { network: true, status: 408 });
      }
      throw new ApiClientError(error instanceof Error ? error.message : 'Network error', {
        network: true,
      });
    } finally {
      clearTimeout(timer);
    }
  };

  // Ensure CSRF before mutating calls
  let csrf: string | null = null;
  if (needsCsrf) {
    csrf = await ensureCsrfToken(false);
  }

  const runWithOptionalCsrfRetry = async (): Promise<Response> => {
    let response = await doFetch(csrf);

    // One automatic CSRF refresh + retry for invalid/missing token
    if (needsCsrf && response.status === 403) {
      const text = await response
        .clone()
        .text()
        .catch(() => '');
      if (isCsrfFailure(response.status, text)) {
        csrf = await ensureCsrfToken(true);
        // Prefer server-issued token from error body if ensure didn't get one
        try {
          const errBody = JSON.parse(text) as { csrfToken?: string };
          if (errBody.csrfToken) {
            csrf = errBody.csrfToken;
            cachedCsrfToken = errBody.csrfToken;
          }
        } catch {
          /* ignore */
        }
        if (csrf) {
          response = await doFetch(csrf);
        }
      }
    }

    return response;
  };

  if (shouldRetry) {
    return withRetry(runWithOptionalCsrfRetry, { maxRetries: 2, baseDelay: 400, maxDelay: 4000 });
  }
  return runWithOptionalCsrfRetry();
}

/**
 * JSON helper — throws ApiClientError on non-OK.
 */
export async function apiJson<T = unknown>(
  path: string,
  options: ApiFetchOptions = {}
): Promise<T> {
  const response = await apiFetch(path, options);
  let data: unknown = null;
  const text = await response.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { raw: text };
    }
  }

  if (!response.ok) {
    const msg =
      (data as { message?: string; error?: string | { message?: string } })?.message ||
      (typeof (data as { error?: unknown })?.error === 'string'
        ? (data as { error: string }).error
        : (data as { error?: { message?: string } })?.error?.message) ||
      `Request failed (${response.status})`;
    throw new ApiClientError(String(msg), { status: response.status, body: data });
  }

  return data as T;
}
